/**
 * SavedAddressSelector — address book radio list shown inside the
 * shipping-address form when the buyer has ≥1 saved address (Sprint 139
 * PR-H: Saved Shipping Addresses). The radio list lets the buyer pick a
 * previously-saved address for pre-fill OR pick "Usar una nueva dirección"
 * to type a new one manually.
 *
 * SPEC-244 · partición VERBATIM de
 * frontend/src/components/market/checkout-modal.tsx (2.637 L).
 *
 * When `savedAddressesLoading` is true, shows a "Cargando…" placeholder.
 * On selection change, calls `onSelect(savedAddressId)` with the
 * selected id OR `'new'` for the new-address option (the parent hook
 * pre-fills the form state accordingly).
 */

import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import type { SavedAddressResponse } from '@profileia/shared';

// ─── Component ───────────────────────────────────────────────────────────────

export interface SavedAddressSelectorProps {
  /** The buyer's saved addresses (empty array if fetch hasn't completed). */
  savedAddresses: SavedAddressResponse[];
  /** True while the address book is being fetched. */
  savedAddressesLoading: boolean;
  /** Current selection — either a `savedAddressId` UUID or the literal `'new'`. */
  selectedSavedAddressId: string;
  /** Called when the buyer picks an address OR switches to `'new'`. */
  onSelect: (savedAddressId: string) => void;
}

export function SavedAddressSelector({
  savedAddresses,
  savedAddressesLoading,
  selectedSavedAddressId,
  onSelect,
}: SavedAddressSelectorProps) {
  return (
    <div className="space-y-2 rounded-md border border-border/60 bg-muted/30 p-3">
      <Label className="text-xs font-medium">
        Tus direcciones guardadas
      </Label>
      {savedAddressesLoading ? (
        <p className="text-xs text-muted-foreground">Cargando…</p>
      ) : (
        <RadioGroup
          value={selectedSavedAddressId}
          onValueChange={onSelect}
          className="space-y-1.5"
        >
          {savedAddresses.map((addr) => (
            <label
              key={addr.savedAddressId}
              htmlFor={`addr-${addr.savedAddressId}`}
              className="flex cursor-pointer items-start gap-2 rounded-md border border-transparent p-2 text-sm hover:border-border/60 hover:bg-background"
            >
              <RadioGroupItem
                id={`addr-${addr.savedAddressId}`}
                value={addr.savedAddressId}
                className="mt-0.5"
              />
              <div className="min-w-0 flex-1">
                <p className="font-medium">{addr.label}</p>
                <p className="text-xs text-muted-foreground">
                  {addr.name} · {addr.phone}
                </p>
                <p className="text-xs text-muted-foreground">
                  {addr.address}, {addr.city}, {addr.country} {addr.zip}
                </p>
              </div>
            </label>
          ))}
          {/* "Usar nueva dirección" option */}
          <label
            htmlFor="addr-new"
            className="flex cursor-pointer items-start gap-2 rounded-md border border-transparent p-2 text-sm hover:border-border/60 hover:bg-background"
          >
            <RadioGroupItem
              id="addr-new"
              value="new"
              className="mt-0.5"
            />
            <div className="min-w-0 flex-1">
              <p className="font-medium">Usar una nueva dirección</p>
              <p className="text-xs text-muted-foreground">
                Ingresá los datos manualmente
              </p>
            </div>
          </label>
        </RadioGroup>
      )}
    </div>
  );
}
